import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, User, Search, Phone, Briefcase, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SgPersonalSoporte() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/servicios-generales/personal-soporte`, { headers });
      setRows(r.data);
    } catch (_) { /* noop */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      (r.nombre || '').toLowerCase().includes(term) ||
      (r.rut || '').toLowerCase().includes(term) ||
      (r.cargo || '').toLowerCase().includes(term) ||
      (r.empresa || '').toLowerCase().includes(term)
    );
  }, [q, rows]);

  const del = async (p) => {
    if (!window.confirm(`Eliminar a ${p.nombre}?`)) return;
    try {
      await axios.delete(`${API}/servicios-generales/personal-soporte/${p.id}`, { headers });
      toast.success('Eliminado.'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="sg-personal-soporte-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Personal de Soporte</h1>
          <p className="text-sm text-slate-500">Personal que ejecuta aseo, mantención u otros servicios en las habitaciones.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar por nombre, RUT, cargo o empresa…"
              className="pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-72" data-testid="input-buscar-personal" />
          </div>
          <button onClick={() => setModal({ mode: 'create' })}
            className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-lg"
            data-testid="btn-nuevo-personal">
            <Plus className="w-4 h-4" /> Nuevo
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">RUT</th>
              <th className="text-left px-4 py-2 font-semibold">Nombre</th>
              <th className="text-left px-4 py-2 font-semibold">Cargo</th>
              <th className="text-left px-4 py-2 font-semibold">Empresa</th>
              <th className="text-left px-4 py-2 font-semibold">Teléfono</th>
              <th className="text-center px-4 py-2 font-semibold">Activo</th>
              <th className="text-right px-4 py-2 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-8">Cargando…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-8">
                {rows.length === 0
                  ? 'Sin personal registrado. Usa el botón "Nuevo" para agregar.'
                  : 'Sin coincidencias.'}
              </td></tr>
            ) : filtered.map(p => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-2 font-mono text-slate-700">{p.rut}</td>
                <td className="px-4 py-2 font-bold text-slate-800 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs">
                    {p.nombre.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                  </div>
                  {p.nombre}
                </td>
                <td className="px-4 py-2 text-slate-600">{p.cargo || '—'}</td>
                <td className="px-4 py-2 text-slate-600">{p.empresa || '—'}</td>
                <td className="px-4 py-2 text-slate-600 font-mono text-xs">{p.telefono || '—'}</td>
                <td className="px-4 py-2 text-center">
                  {p.activo
                    ? <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">ACTIVO</span>
                    : <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">INACTIVO</span>}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setModal({ mode: 'edit', data: p })} className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <ModalPersonal modal={modal} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }} headers={headers} />
      )}
    </div>
  );
}

function ModalPersonal({ modal, onClose, onSaved, headers }) {
  const isEdit = modal.mode === 'edit';
  const d = modal.data;
  const [form, setForm] = useState({
    rut: d?.rut || '',
    nombre: d?.nombre || '',
    cargo: d?.cargo || '',
    empresa: d?.empresa || '',
    telefono: d?.telefono || '',
    activo: d?.activo ?? true,
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.rut.trim()) { toast.error('RUT obligatorio'); return; }
    if (!form.nombre.trim()) { toast.error('Nombre obligatorio'); return; }
    try {
      if (isEdit) await axios.put(`${API}/servicios-generales/personal-soporte/${d.id}`, form, { headers });
      else await axios.post(`${API}/servicios-generales/personal-soporte`, form, { headers });
      toast.success('Guardado.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{isEdit ? 'Editar' : 'Nuevo'} personal</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-semibold">RUT *</label>
              <input value={form.rut} onChange={e => setForm({ ...form, rut: e.target.value })}
                placeholder="12345678-9" autoFocus
                className="w-full px-3 py-2 text-sm border rounded-lg font-mono" data-testid="input-personal-rut" />
            </div>
            <div className="flex items-end gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} />
                Activo
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><User className="w-3 h-3" /> Nombre *</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
              placeholder="Nombre completo"
              className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-personal-nombre" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Briefcase className="w-3 h-3" /> Cargo</label>
              <input value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })}
                placeholder="Camarera, Técnico eléctrico…"
                className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-personal-cargo" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Building2 className="w-3 h-3" /> Empresa</label>
              <input value={form.empresa} onChange={e => setForm({ ...form, empresa: e.target.value })}
                placeholder="Empresa contratista"
                className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-personal-empresa" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Phone className="w-3 h-3" /> Teléfono</label>
            <input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })}
              placeholder="+56 9 …"
              className="w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold" data-testid="btn-guardar-personal">
              {isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
